import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, ChevronDown, Download, XCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  useFormatkaTemplateCheck,
  useFormatkaTemplates,
  useGenerateSheetSet,
  useSaveFormatkaProjectFields,
  useSheetSet,
  useTaskPolling,
  useUpdateSheetSet,
} from "../../hooks";
import type { FormatkaSheetSet, FormatkaType, PowerProject } from "../../types";
import { FORMATKA_PAPER_LABEL, FORMATKA_TYPE_LABEL } from "../../types";
import { operatorOptionBadge, sortByOperator } from "../../operatorTemplates";

/** Template + fields + generate step — the GenerateConsentModal pattern:
 *  pick template → variable-availability check → editable field overrides →
 *  PATCH → POST generate → live task log → download ZIP. */
export function GenerateFormatkiModal({
  sheetSet,
  connection,
  onClose,
}: {
  sheetSet: FormatkaSheetSet;
  connection: PowerProject;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: templates, isLoading: templatesLoading } = useFormatkaTemplates();
  const [templateId, setTemplateId] = useState(sheetSet.template ?? "");
  const check = useFormatkaTemplateCheck(templateId, sheetSet.id);
  const update = useUpdateSheetSet(sheetSet.id);
  const generate = useGenerateSheetSet(sheetSet.id);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);
  const taskStatus = task.data?.status;
  const running =
    update.isPending ||
    generate.isPending ||
    (!!taskId && !["success", "failure", "cancelled"].includes(taskStatus ?? ""));

  const saveProjectFields = useSaveFormatkaProjectFields(sheetSet.connection);

  const [fields, setFields] = useState<Record<string, string>>(() => ({ ...sheetSet.field_values }));
  const [includeOrientation, setIncludeOrientation] = useState(sheetSet.include_orientation ?? true);
  const [formatkaType, setFormatkaType] = useState<FormatkaType>(sheetSet.formatka_type ?? "plan_sytuacyjny");
  const [numberAdjacent, setNumberAdjacent] = useState(sheetSet.number_adjacent_sheets ?? true);
  const [rememberForProject, setRememberForProject] = useState(true);
  // Pola wpisu bierzemy Z SZABLONU (check → fields), nie z listy wpisanej w UI:
  // wpis trafia do field_values, a te wypełniają WYŁĄCZNIE #zmienne#, które szablon
  // ma. Sztywna lista („Branża", „Jednostka projektowa"…) kazała wpisywać wartości,
  // dla których typowa formatka nie ma miejsca, i nie dawała dojścia do tych, które
  // ma (#projektant_glowny_uprawnienia# ze słownika projektantów).
  const editableFields = useMemo(
    () => (check.data?.fields ?? []).filter((f) => f.editable),
    [check.data],
  );
  const autoFields = useMemo(
    () => (check.data?.fields ?? []).filter((f) => !f.editable),
    [check.data],
  );
  // null = użytkownik jeszcze nie klikał → lista jest zwinięta, dopóki wszystko się
  // zgadza, i rozwija się sama, gdy szablonowi brakuje zmiennej (to blokuje generowanie).
  const [fieldsOpen, setFieldsOpen] = useState<boolean | null>(null);
  const showFields = fieldsOpen ?? !(check.data?.ok ?? true);
  const missingCount = editableFields.filter((f) => f.status === "missing").length;
  // Zwinięta lista nie może chować tego, co wpisano — inaczej wpis wyjechałby na
  // formatkę bez śladu w oknie. Podsumowanie pokazuje wpisy pól TEGO szablonu.
  const typedFields = editableFields.filter((f) => (fields[f.name] ?? "").trim() !== "");

  // Refresh the sheet set (status/output_file) when the task settles.
  const { refetch: refetchSheetSet } = useSheetSet(sheetSet.id);
  useEffect(() => {
    if (taskId && taskStatus && ["success", "failure", "cancelled"].includes(taskStatus)) {
      refetchSheetSet();
      qc.invalidateQueries({ queryKey: ["pp", "formatka-sheet-sets", sheetSet.connection] });
    }
  }, [taskId, taskStatus, qc, refetchSheetSet, sheetSet.connection]);

  const cleanedFields = () => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(fields)) if (v.trim() !== "") out[k] = v;
    return out;
  };

  // Zapis „zapamiętaj dla projektu" — czekamy na niego, żeby POST nie zginął przy
  // zamknięciu modala; best-effort.
  const persistFields = async () => {
    if (!rememberForProject) return;
    const values = cleanedFields();
    if (Object.keys(values).length) {
      try {
        await saveProjectFields.mutateAsync(values);
      } catch {
        /* zapis best-effort */
      }
    }
  };

  const closeWithPersist = async () => {
    await persistFields();
    onClose();
  };

  const start = async () => {
    if (!templateId) return;
    // Persist the picked template + entered fields, then dispatch. The check
    // re-runs server-side inside generate — a stale-check race just 400s with
    // the missing list.
    const cleaned = cleanedFields();
    await persistFields();
    update.mutate(
      {
        template: templateId, field_values: cleaned,
        include_orientation: includeOrientation,
        formatka_type: formatkaType,
        number_adjacent_sheets: numberAdjacent,
      },
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: ["pp", "formatka-check"] });
          generate.mutate(undefined, { onSuccess: (r) => setTaskId(r.task_id) });
        },
      },
    );
  };

  const done = taskStatus === "success";
  const failed = taskStatus === "failure" || generate.isError || update.isError;
  const { data: freshSet } = useSheetSet(sheetSet.id);
  const output = freshSet?.output_file ?? sheetSet.output_file;

  return (
    <Modal open onClose={closeWithPersist} title={`Generuj formatki — ${connection.project_name ?? ""}`} size="lg">
      <div className="space-y-3">
        {/* Dla jakiej gminy powstaje mapa — na jednym terenie bywają dwie różne
            gminy, a zestaw urzędowy trafia do urzędu konkretnej gminy. */}
        {(sheetSet.communes ?? []).length > 0 && (
          <p
            className={
              sheetSet.mode === "parcel_office" && (sheetSet.communes ?? []).length > 1
                ? "rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700"
                : "rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] px-2 py-1 text-xs text-[var(--color-text-secondary)]"
            }
          >
            {sheetSet.mode === "parcel_office" && (sheetSet.communes ?? []).length > 1 ? (
              <>
                ⚠ Działki zestawu leżą w <b>{(sheetSet.communes ?? []).length} gminach</b>:{" "}
                <b>{(sheetSet.communes ?? []).join(", ")}</b> — mapa dla urzędu zwykle
                obejmuje jedną gminę.
              </>
            ) : (
              <>
                Mapa generowana dla gminy: <b>{(sheetSet.communes ?? []).join(", ")}</b>
              </>
            )}
          </p>
        )}
        <label className="block">
          <span className="section-label">Szablon formatki *</span>
          {templatesLoading ? (
            <div className="py-2"><Spinner className="h-4 w-4" /></div>
          ) : (
            <select
              className="input-base mt-1"
              value={templateId}
              disabled={running}
              onChange={(e) => { setTemplateId(e.target.value); setTaskId(null); }}
            >
              <option value="">— wybierz szablon —</option>
              {sortByOperator(templates ?? [], connection.operator).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.is_company ? "★ " : ""}{t.name} ({FORMATKA_PAPER_LABEL[t.paper]}){" "}
                  {operatorOptionBadge(t.operator, connection.operator)}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="block">
          <span className="section-label">Typ formatki</span>
          <select
            className="input-base mt-1"
            value={formatkaType}
            disabled={running}
            onChange={(e) => setFormatkaType(e.target.value as FormatkaType)}
          >
            {(Object.keys(FORMATKA_TYPE_LABEL) as FormatkaType[]).map((t) => (
              <option key={t} value={t}>{FORMATKA_TYPE_LABEL[t]}</option>
            ))}
          </select>
          <span className="mt-1 block text-[11px] text-[var(--color-text-muted)]">
            Tytuł metryki: koncepcja → uzgodnienia koncepcji, plan sytuacyjny → uzgodnienia MZ,
            PZT → projekt na podkładzie MDCP.
          </span>
        </label>

        {templateId && (
          <div className="text-xs">
            {check.isLoading ? (
              <p className="flex items-center gap-2 text-[var(--color-text-muted)]">
                <Spinner className="h-3 w-3" /> Sprawdzanie dostępności zmiennych…
              </p>
            ) : check.data?.ok ? (
              <p className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                <CheckCircle2 size={13} /> Wszystkie zmienne szablonu są dostępne dla tego zestawu.
              </p>
            ) : check.data ? (
              <p className="flex items-center gap-1.5 text-[var(--color-primary)]">
                <XCircle size={13} /> Uzupełnij pola oznaczone „brak” poniżej — bez nich
                generowanie jest zablokowane.
              </p>
            ) : check.isError ? (
              <p className="text-[var(--color-primary)]">Nie udało się sprawdzić szablonu.</p>
            ) : null}
          </div>
        )}

        {/* Pola = #zmienne# TEGO szablonu. Wpis wygrywa ze wszystkim, co zapisane
            gdzie indziej (słownik projektantów, karta projektu, defaulty); puste
            pole zostawia wartość z systemu — pokazaną w podpowiedzi. */}
        {templateId && check.data && (
          <div className="space-y-1.5">
            <button
              type="button"
              onClick={() => setFieldsOpen(!showFields)}
              aria-expanded={showFields}
              className="flex w-full items-center justify-between gap-2 text-left"
            >
              <span className="section-label">
                Pola tabelki tego szablonu
                <span className="ml-1.5 font-normal normal-case text-[var(--color-text-muted)]">
                  ({editableFields.length})
                </span>
                {missingCount > 0 && (
                  <span className="ml-1.5 font-normal normal-case text-[var(--color-primary)]">
                    · {missingCount} do uzupełnienia
                  </span>
                )}
              </span>
              <ChevronDown
                size={14}
                className={`shrink-0 text-[var(--color-text-muted)] transition-transform ${showFields ? "rotate-180" : ""}`}
              />
            </button>
            {!showFields && typedFields.length > 0 && (
              <p className="text-[10px] text-[var(--color-text-muted)]">
                Wpisane:{" "}
                {typedFields.map((f) => `${f.label} = ${fields[f.name]}`).join(" · ")}
              </p>
            )}
            {showFields && (editableFields.length === 0 ? (
              <p className="text-xs text-[var(--color-text-muted)]">
                Ten szablon nie ma żadnej zmiennej do ręcznego wpisania — wszystko liczy
                się automatycznie.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {editableFields.map((f) => (
                  <label key={f.name} className="block">
                    <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
                      <span className="truncate">{f.label}</span>
                      {f.status === "missing" && (
                        <span className="text-[var(--color-primary)] normal-case">brak</span>
                      )}
                    </span>
                    <input
                      className="input-base mt-0.5 text-xs"
                      value={fields[f.name] ?? ""}
                      disabled={running}
                      placeholder={f.value ? `z systemu: ${f.value}` : "puste — wpisz, jeśli ma się wydrukować"}
                      onChange={(e) => setFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
                    />
                    <span className="mt-0.5 block text-[10px] text-[var(--color-text-muted)]">
                      <span className="font-mono">#{f.name}#</span>
                      {f.source ? ` — ${f.source}` : ""}
                    </span>
                  </label>
                ))}
              </div>
            ))}
            {showFields && autoFields.length > 0 && (
              <p className="text-[10px] text-[var(--color-text-muted)]">
                Liczone przy generacji, osobno dla każdego arkusza/działki (bez pola wpisu):{" "}
                {autoFields.map((f) => `#${f.name}#`).join(", ")}
              </p>
            )}
          </div>
        )}
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={includeOrientation}
            disabled={running}
            onChange={(e) => setIncludeOrientation(e.target.checked)}
          />
          Arkusz orientacyjny — poprzedza arkusze i pokazuje ich rozkład (obrysy +
          numery). W paczce każda działka dostaje własny, ze swoimi arkuszami
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={numberAdjacent}
            disabled={running}
            onChange={(e) => setNumberAdjacent(e.target.checked)}
          />
          Numeruj sąsiednie arkusze — przy krawędziach dopisz numer stykającego się
          arkusza (arkusze, które się nie stykają ani nakładają, nie są opisywane;
          w paczce tylko arkusze tej samej działki)
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={rememberForProject}
            disabled={running}
            onChange={(e) => setRememberForProject(e.target.checked)}
          />
          Zapamiętaj wpisane wartości dla tego projektu
        </label>
        <p className="text-[10px] font-mono text-[var(--color-text-muted)]">
          Kolejność: wpis w tym oknie (wygrywa zawsze) → dane z systemu (słownik
          projektantów, karta projektu, klient) → wartość zapamiętana dla projektu →
          domyślna globalna. Dwa ostatnie źródła wypełniają tylko PUSTE pola. Skala i
          numeracja arkuszy wypełniane są per arkusz automatycznie.
        </p>

        {taskId && task.data && (
          <div className="border-t border-[var(--color-border)] pt-2">
            <div className="flex items-start gap-2 mb-1.5">
              {["pending", "running"].includes(task.data.status) && <Spinner />}
              <p className="text-xs font-mono">
                {done ? (
                  <span className="text-emerald-600 inline-flex items-center gap-2">
                    ✓ Paczka gotowa.
                    {output?.url && (
                      <a
                        href={output.url}
                        download={output.original_filename}
                        className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
                      >
                        <Download size={12} /> Pobierz ZIP
                      </a>
                    )}
                  </span>
                ) : task.data.status === "failure" ? (
                  <span className="text-[var(--color-primary)]">
                    Błąd generowania — log poniżej. Popraw, co trzeba (szablon, pola), i kliknij
                    „Ponów generowanie”.
                  </span>
                ) : (
                  <span className="text-[var(--color-text-secondary)]">
                    Serwer składa arkusze (layouty DXF + skrypt plotu)… postęp poniżej.
                  </span>
                )}
              </p>
            </div>
            <pre className="whitespace-pre-wrap max-h-40 overflow-y-auto text-[11px] font-mono text-[var(--color-text-secondary)] bg-[var(--color-bg-elevated)] p-2 rounded">
              {task.data.log || "…"}
            </pre>
          </div>
        )}
        {failed && !taskId && (
          <p className="text-xs text-[var(--color-primary)]">Nie udało się uruchomić generowania.</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={closeWithPersist}>Zamknij</Button>
          <Button size="sm" disabled={!templateId || running} loading={running} onClick={start}>
            {taskStatus === "failure" || (!taskId && sheetSet.status === "failure")
              ? "Ponów generowanie"
              : "Generuj"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
